import { EDocumentType, EFinancingProgram, getEFinancingProgramList } from '@if/core/types'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  BulkDocumentExportCommandDto,
  BulkExportTaskListItemDto,
  useBulkDocumentExportTasks,
  useStartBulkDocumentExport,
} from '@src/data/api/bulk-document-export/bulk-document-export-api'
import { useCurrentLang, useDocumentTypes } from '@src/data/selectors/app-config-selectors'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { BulkExportErrorBreakdownPopover, useBulkExportTaskColumns } from './BulkDocumentExportPage-hooks'

const PROGRAM_DEFAULT_DOCUMENT_TYPES: Partial<Record<EFinancingProgram, EDocumentType[]>> = {
  [EFinancingProgram.Personal]: [EDocumentType.AuditCVT, EDocumentType.SignedCVT],
  [EFinancingProgram.AutoRsla]: [EDocumentType.ThirdPartyContract],
}

const resolveSiteLanguage = (lang: string): string => (lang?.toLowerCase().startsWith('fr') ? 'fr' : 'en')

interface DocTypeOption {
  id: EDocumentType
  label: string
  group: string
}

const BulkDocumentExportPage = (): JSX.Element => {
  const { t, i18n } = useTranslation()
  const user = useUser()
  const lang = useCurrentLang()
  const [start, isStarting] = useStartBulkDocumentExport()
  const [tasks, isLoadingTasks, refreshTasks] = useBulkDocumentExportTasks(true, 30)
  const [errorPopover, setErrorPopover] = useState<{ anchor: HTMLElement; row: BulkExportTaskListItemDto } | null>(null)
  const handleOpenErrors = useCallback((anchor: HTMLElement, row: BulkExportTaskListItemDto) => {
    setErrorPopover({ anchor, row })
  }, [])
  const taskColumns = useBulkExportTaskColumns({ onOpenErrors: handleOpenErrors })

  const [trancheId, setTrancheId] = useState<string>('')
  const [loanIdsRaw, setLoanIdsRaw] = useState<string>('')
  const [financingProgramId, setFinancingProgramId] = useState<EFinancingProgram>(EFinancingProgram.AutoRsla)
  const [documentTypeIds, setDocumentTypeIds] = useState<EDocumentType[]>(
    PROGRAM_DEFAULT_DOCUMENT_TYPES[EFinancingProgram.AutoRsla] ?? [],
  )
  const [language, setLanguage] = useState<string>(resolveSiteLanguage(i18n.language))
  const [submitError, setSubmitError] = useState<string>('')
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false)

  const documentTypes = useDocumentTypes(financingProgramId)

  useEffect(() => {
    setDocumentTypeIds(PROGRAM_DEFAULT_DOCUMENT_TYPES[financingProgramId] ?? [])
  }, [financingProgramId])

  const programOptions = useMemo(() => getEFinancingProgramList(), [])

  const documentTypeOptions = useMemo<DocTypeOption[]>(() => {
    const defaults = PROGRAM_DEFAULT_DOCUMENT_TYPES[financingProgramId] ?? []
    const defaultsSet = new Set<EDocumentType>(defaults)

    const groupDefault = t('bulkDocumentExport.documentTypes.groupDefault')
    const groupOther = t('bulkDocumentExport.documentTypes.groupOther')

    const opts = Object.values(documentTypes).map<DocTypeOption>((dt) => ({
      id: dt.id as EDocumentType,
      label: dt[lang] ?? String(dt.id),
      group: defaultsSet.has(dt.id as EDocumentType) ? groupDefault : groupOther,
    }))

    return opts.sort((a, b) => {
      if (a.group !== b.group) return a.group === groupDefault ? -1 : 1
      return a.label.localeCompare(b.label, lang)
    })
  }, [documentTypes, financingProgramId, lang, t])

  const selectedOptions = useMemo(
    () => documentTypeOptions.filter((o) => documentTypeIds.includes(o.id)),
    [documentTypeOptions, documentTypeIds],
  )

  const parseList = (raw: string): string[] =>
    raw
      .split(/[,\s\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setSubmitError('')
    setSubmitSuccess(false)

    const loanIds = parseList(loanIdsRaw)
    const trancheNum = trancheId.trim().length > 0 ? parseInt(trancheId, 10) : null

    if (trancheNum === null && loanIds.length === 0) {
      setSubmitError(t('bulkDocumentExport.errors.atLeastOneSource'))
      return
    }
    if (trancheNum !== null && Number.isNaN(trancheNum)) {
      setSubmitError(t('bulkDocumentExport.errors.invalidTrancheId'))
      return
    }

    const command: BulkDocumentExportCommandDto = {
      trancheId: trancheNum,
      loanIds,
      documentTypeIds,
      financingProgramId,
      language,
    }

    try {
      await start(command)
      setSubmitSuccess(true)
    } catch (error) {
      reportErrorToConsole(error)
      setSubmitError(t('bulkDocumentExport.errors.startFailed'))
    }
  }

  return (
    <FullScreenContainer>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">
          {t('bulkDocumentExport.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('bulkDocumentExport.subtitle')}
        </Typography>
      </Box>

      <Chip
        label={t('bulkDocumentExport.resultEmailedTo', { email: user.username })}
        color="info"
        variant="outlined"
        sx={{ mb: 2 }}
      />

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="bulk-export-program-label">{t('bulkDocumentExport.fields.financingProgram')}</InputLabel>
              <Select
                labelId="bulk-export-program-label"
                label={t('bulkDocumentExport.fields.financingProgram')}
                value={financingProgramId}
                onChange={(e) => setFinancingProgramId(e.target.value as EFinancingProgram)}
              >
                {programOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {t(`EFinancingProgram.${p}`, { ns: 'enum', defaultValue: p })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="bulk-export-language-label">{t('bulkDocumentExport.fields.language')}</InputLabel>
              <Select
                labelId="bulk-export-language-label"
                label={t('bulkDocumentExport.fields.language')}
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="fr">Français</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label={t('bulkDocumentExport.fields.trancheId')}
              value={trancheId}
              onChange={(e) => setTrancheId(e.target.value)}
              helperText={t('bulkDocumentExport.fields.trancheIdHelp')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete<DocTypeOption, true, false, false>
              multiple
              disableCloseOnSelect
              options={documentTypeOptions}
              groupBy={(o) => o.group}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedOptions}
              onChange={(_, value) => setDocumentTypeIds(value.map((v) => v.id))}
              renderOption={(props, option, { selected }) => {
                const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string }
                return (
                  <li key={key} {...rest}>
                    <Checkbox checked={selected} sx={{ mr: 1 }} />
                    {option.label}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('bulkDocumentExport.fields.documentTypes')}
                  helperText={t('bulkDocumentExport.fields.documentTypesHelp')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label={t('bulkDocumentExport.fields.loanIds')}
              value={loanIdsRaw}
              onChange={(e) => setLoanIdsRaw(e.target.value)}
              helperText={t('bulkDocumentExport.fields.loanIdsHelp')}
            />
          </Grid>
        </Grid>

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {t('bulkDocumentExport.startedSuccessfully')}
          </Alert>
        )}

        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isStarting}
            startIcon={isStarting ? <CircularProgress size={16} /> : null}
          >
            {t('bulkDocumentExport.actions.start')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" component="h2" sx={{ flex: 1 }}>
          {t('bulkDocumentExport.tasks.title')}
        </Typography>
        <Tooltip title={t('bulkDocumentExport.tasks.refresh')}>
          <span>
            <IconButton onClick={refreshTasks} disabled={isLoadingTasks} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ height: '35rem', width: '100%' }}>
        <DataGrid
          disableColumnFilter
          rows={tasks}
          columns={taskColumns}
          getRowId={(row: BulkExportTaskListItemDto) => row.instanceId}
          loading={isLoadingTasks}
          isRowSelectable={() => false}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
        />
      </Box>

      <BulkExportErrorBreakdownPopover
        anchorEl={errorPopover?.anchor ?? null}
        row={errorPopover?.row ?? null}
        onClose={() => setErrorPopover(null)}
      />
    </FullScreenContainer>
  )
}

export default BulkDocumentExportPage
